import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSettings, resolveImage } from "../lib/settings";
import { Nav } from "../components/Nav";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Trash2, Upload, CheckCircle2, XCircle, Save, Plus } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!user) nav("/login");
    else if (user.role !== "admin") nav("/dashboard");
  }, [user, nav]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-24">
        <p className="overline"><span className="gold-line mr-3" />Studio Administration</p>
        <h1 className="font-serif-display font-light text-4xl md:text-5xl mt-4 mb-12">Manage the course.</h1>

        <Tabs defaultValue="stats">
          <TabsList className="bg-transparent border-b border-black/10 w-full justify-start rounded-none h-auto p-0 mb-10">
            {["stats","content","images","faqs","modules","assignments","students","payments","testimonials","messages"].map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                data-testid={`admin-tab-${t}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] data-[state=active]:bg-transparent data-[state=active]:shadow-none uppercase tracking-widest text-xs px-6 py-4"
              >{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="stats"><StatsPanel /></TabsContent>
          <TabsContent value="content"><ContentPanel /></TabsContent>
          <TabsContent value="images"><ImagesPanel /></TabsContent>
          <TabsContent value="faqs"><FAQsPanel /></TabsContent>
          <TabsContent value="modules"><ModulesPanel /></TabsContent>
          <TabsContent value="assignments"><AssignmentsPanel /></TabsContent>
          <TabsContent value="students"><StudentsPanel /></TabsContent>
          <TabsContent value="payments"><PaymentsPanel /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsPanel /></TabsContent>
          <TabsContent value="messages"><MessagesPanel /></TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

function StatsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div>Loading…</div>;
  const stats = [
    ["Total Students", s.total_students],
    ["Active Enrolments", s.total_enrollments],
    ["USD Revenue", `$${s.revenue_usd.toLocaleString()}`],
    ["INR Revenue", `₹${s.revenue_inr.toLocaleString()}`],
    ["Pending Reviews", s.pending_assignments],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-6" data-testid="admin-stats">
      {stats.map(([l, v]) => (
        <div key={l} className="border border-black/10 bg-white p-6">
          <p className="overline">{l}</p>
          <p className="font-serif-display text-4xl mt-4">{v}</p>
        </div>
      ))}
    </div>
  );
}

function ModulesPanel() {
  const [course, setCourse] = useState(null);
  const load = () => api.get("/course").then((r) => setCourse(r.data));
  useEffect(() => { load(); }, []);
  const uploadRef = useRef({});

  const uploadFile = async (lessonId, kind, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", kind);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const patch = kind === "video" ? { video_path: data.path } : { pdf_path: data.path };
      await api.patch(`/admin/lessons/${lessonId}`, patch);
      toast.success(`${kind === "video" ? "Video" : "PDF"} uploaded`);
      load();
    } catch { toast.error("Upload failed"); }
  };

  const saveEmbedUrl = async (lessonId, url) => {
    try {
      await api.patch(`/admin/lessons/${lessonId}`, { video_embed_url: url || null });
      toast.success(url ? "Embed URL saved" : "Embed URL cleared");
      load();
    } catch { toast.error("Failed to save embed URL"); }
  };

  if (!course) return <div>Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-modules">
      {course.modules.map((m, i) => (
        <div key={m.id} className="border border-black/10 bg-white p-6">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="overline">Module {String(i+1).padStart(2, '0')}</p>
              <h3 className="font-serif-display text-2xl mt-1">{m.title}</h3>
            </div>
          </div>
          <ul className="mt-6 divide-y divide-black/5">
            {m.lessons.map((l) => (
              <li key={l.id} className="py-3 flex flex-col gap-3" data-testid={`admin-lesson-${l.id}`}>
                <div className="flex flex-wrap items-center gap-4 justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{l.title}</p>
                    <p className="overline text-[10px] mt-1">
                      {l.video_embed_url ? <span className="text-[#D4AF37]">Embed ✓</span> : l.video_path ? <span className="text-[#D4AF37]">Video ✓</span> : "No video"} · {l.pdf_path ? <span className="text-[#D4AF37]">PDF ✓</span> : "No PDF"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <label className="btn-editorial-outline cursor-pointer" data-testid={`upload-video-${l.id}`}>
                      <Upload size={14} className="mr-2" />Video
                      <input hidden type="file" accept="video/*" onChange={(e) => e.target.files[0] && uploadFile(l.id, "video", e.target.files[0])} />
                    </label>
                    <label className="btn-editorial-outline cursor-pointer" data-testid={`upload-pdf-${l.id}`}>
                      <Upload size={14} className="mr-2" />PDF
                      <input hidden type="file" accept="application/pdf" onChange={(e) => e.target.files[0] && uploadFile(l.id, "pdf", e.target.files[0])} />
                    </label>
                  </div>
                </div>
                <EmbedUrlEditor lesson={l} onSave={saveEmbedUrl} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function EmbedUrlEditor({ lesson, onSave }) {
  const [value, setValue] = useState(lesson.video_embed_url || "");
  const isDirty = (value || "") !== (lesson.video_embed_url || "");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`embed-url-row-${lesson.id}`}>
      <input
        data-testid={`embed-url-input-${lesson.id}`}
        type="url"
        placeholder="Paste Bunny Stream / iframe embed URL (optional)"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="flex-1 min-w-[240px] text-xs border border-black/15 bg-[#FAFAFA] px-3 py-2 outline-none focus:border-black"
      />
      <button
        data-testid={`embed-url-save-${lesson.id}`}
        disabled={!isDirty}
        onClick={() => onSave(lesson.id, value.trim())}
        className={`text-[10px] uppercase tracking-widest px-3 py-2 border ${isDirty ? 'bg-black text-white border-black' : 'text-black/30 border-black/15'}`}
      >Save</button>
      {lesson.video_embed_url && (
        <button
          data-testid={`embed-url-clear-${lesson.id}`}
          onClick={() => { setValue(""); onSave(lesson.id, ""); }}
          className="text-[10px] uppercase tracking-widest px-3 py-2 border border-black/15"
        >Clear</button>
      )}
    </div>
  );
}


function AssignmentsPanel() {
  const [items, setItems] = useState([]);
  const [feedback, setFeedback] = useState({});
  const load = () => api.get("/admin/assignments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const review = async (a, status) => {
    try {
      await api.post(`/admin/assignments/${a.user_id}/${a.course_id}/review`, {
        status, feedback: feedback[a.user_id] || "",
      });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-6" data-testid="admin-assignments">
      {items.length === 0 && <p className="text-sm text-[#6B6B6B]">No assignments submitted yet.</p>}
      {items.map((a, i) => (
        <div key={i} className="border border-black/10 bg-white p-6" data-testid={`admin-assignment-${a.user_id}`}>
          <div className="flex items-baseline justify-between">
            <div>
              <p className="overline">{a.user?.name} · {a.user?.email}</p>
              <p className="text-xs text-[#6B6B6B] mt-1">Submitted: {new Date(a.submitted_at).toLocaleDateString()}</p>
            </div>
            <span className={`overline ${a.status === "approved" ? "text-[#D4AF37]" : a.status === "rejected" ? "text-red-600" : ""}`}>{a.status}</span>
          </div>
          <p className="mt-4 text-sm whitespace-pre-wrap">{a.submission_text}</p>
          {a.status === "pending_review" && (
            <div className="mt-4 space-y-3">
              <textarea
                placeholder="Feedback (optional)"
                rows={2}
                value={feedback[a.user_id] || ""}
                onChange={(e) => setFeedback({ ...feedback, [a.user_id]: e.target.value })}
                className="w-full border border-black/15 p-3 text-sm"
              />
              <div className="flex gap-3">
                <button onClick={() => review(a, "approved")} data-testid={`approve-${a.user_id}`} className="btn-editorial">
                  <CheckCircle2 size={14} className="mr-2" />Approve
                </button>
                <button onClick={() => review(a, "rejected")} data-testid={`reject-${a.user_id}`} className="btn-editorial-outline">
                  <XCircle size={14} className="mr-2" />Reject
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StudentsPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/students").then((r) => setRows(r.data)); }, []);
  return (
    <div className="border border-black/10 bg-white overflow-x-auto" data-testid="admin-students">
      <table className="w-full text-sm">
        <thead className="border-b border-black/10">
          <tr className="text-left"><th className="p-4 overline">Name</th><th className="p-4 overline">Email</th><th className="p-4 overline">Enrolled</th><th className="p-4 overline">Joined</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-black/5">
              <td className="p-4">{r.name}</td>
              <td className="p-4">{r.email}</td>
              <td className="p-4">{r.enrolled ? <span className="text-[#D4AF37]">✓ Active</span> : "—"}</td>
              <td className="p-4 text-[#6B6B6B]">{new Date(r.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setRows(r.data)); }, []);
  return (
    <div className="border border-black/10 bg-white overflow-x-auto" data-testid="admin-payments">
      <table className="w-full text-sm">
        <thead className="border-b border-black/10">
          <tr className="text-left"><th className="p-4 overline">Student</th><th className="p-4 overline">Amount</th><th className="p-4 overline">Status</th><th className="p-4 overline">Date</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.session_id} className="border-b border-black/5">
              <td className="p-4">{r.user?.name || "—"}<br /><span className="text-xs text-[#6B6B6B]">{r.user?.email}</span></td>
              <td className="p-4">{r.currency === "inr" ? "₹" : "$"}{r.amount.toLocaleString()}</td>
              <td className="p-4"><span className={r.payment_status === "paid" ? "text-[#D4AF37]" : "text-[#6B6B6B]"}>{r.payment_status}</span></td>
              <td className="p-4 text-[#6B6B6B]">{new Date(r.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TestimonialsPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ student_name: "", role: "", quote: "", rating: 5, is_published: true });
  const load = () => api.get("/admin/testimonials").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/testimonials", form); toast.success("Added"); load(); setForm({ ...form, student_name: "", role: "", quote: "" }); }
    catch { toast.error("Failed"); }
  };
  const togglePublish = async (t) => {
    try { await api.put(`/admin/testimonials/${t.id}`, { is_published: !t.is_published }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); load(); };
  const pending = rows.filter((t) => !t.is_published);
  const published = rows.filter((t) => t.is_published);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="admin-testimonials">
      <form onSubmit={add} className="border border-black/10 bg-white p-6 space-y-4 h-fit">
        <p className="overline"><span className="gold-line mr-3" />Add Testimonial</p>
        <input required placeholder="Student name" value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} className="w-full border-b border-black/20 py-2 outline-none focus:border-black" />
        <input placeholder="Role (e.g. NIFT Graduate)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border-b border-black/20 py-2 outline-none focus:border-black" />
        <textarea required rows={3} placeholder="Quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="w-full border border-black/15 p-3 text-sm" />
        <button className="btn-editorial">Add</button>
      </form>
      <div className="space-y-6">
        {pending.length > 0 && (
          <div>
            <p className="overline mb-3 text-[#D4AF37]"><span className="gold-line mr-3" />Pending Approval ({pending.length})</p>
            <div className="space-y-4">
              {pending.map((t) => (
                <div key={t.id} className="border border-[#D4AF37]/40 bg-[#F2F0EB] p-5" data-testid={`pending-testimonial-${t.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium">{t.student_name}</p>
                      <p className="overline text-[10px] mt-1">{t.role || "—"}</p>
                    </div>
                    <button onClick={() => del(t.id)} className="text-[#6B6B6B] hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                  <p className="text-sm italic mt-3">"{t.quote}"</p>
                  <button onClick={() => togglePublish(t)} data-testid={`approve-testimonial-${t.id}`} className="btn-editorial mt-4">
                    <CheckCircle2 size={14} className="mr-2" />Approve & Publish
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {published.length > 0 && (
          <div>
            <p className="overline mb-3">Published ({published.length})</p>
            <div className="space-y-4">
              {published.map((t) => (
                <div key={t.id} className="border border-black/10 bg-white p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium">{t.student_name}</p>
                      <p className="overline text-[10px] mt-1">{t.role}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => togglePublish(t)} className="overline link-underline">Unpublish</button>
                      <button onClick={() => del(t.id)} className="text-[#6B6B6B] hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  <p className="text-sm italic mt-3">"{t.quote}"</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {rows.length === 0 && <p className="text-sm text-[#6B6B6B]">No testimonials yet — student reviews will appear here for approval.</p>}
      </div>
    </div>
  );
}

function MessagesPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/contact-messages").then((r) => setRows(r.data)); }, []);
  return (
    <div className="space-y-4" data-testid="admin-messages">
      {rows.length === 0 && <p className="text-sm text-[#6B6B6B]">No messages.</p>}
      {rows.map((m) => (
        <div key={m.id} className="border border-black/10 bg-white p-5">
          <div className="flex justify-between">
            <p className="text-sm font-medium">{m.name}</p>
            <p className="overline text-[10px]">{new Date(m.created_at).toLocaleDateString()}</p>
          </div>
          <p className="text-xs text-[#6B6B6B]">{m.email}</p>
          <p className="mt-3 text-sm whitespace-pre-wrap">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

// ==================== CONTENT PANEL ====================
function ContentPanel() {
  const { settings, reload } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);

  if (!form) return <div>Loading…</div>;

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Parse learn list if it's a string
      if (typeof payload.course_what_you_learn === "string") {
        payload.course_what_you_learn = payload.course_what_you_learn
          .split("\n").map((s) => s.trim()).filter(Boolean);
      }
      payload.price_inr = Number(payload.price_inr);
      payload.price_usd = Number(payload.price_usd);
      await api.put("/admin/site-settings", payload);
      await reload();
      toast.success("Content saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const learnStr = Array.isArray(form.course_what_you_learn)
    ? form.course_what_you_learn.join("\n") : (form.course_what_you_learn || "");

  const Section = ({ title, children }) => (
    <div className="border border-black/10 bg-white p-6 mb-6">
      <p className="overline mb-6"><span className="gold-line mr-3" />{title}</p>
      <div className="space-y-4">{children}</div>
    </div>
  );

  const Field = ({ label, k, multi, type = "text", helper }) => (
    <div>
      <label className="overline block mb-2">{label}</label>
      {multi ? (
        <textarea
          data-testid={`content-${k}`}
          rows={4}
          value={form[k] || ""}
          onChange={(e) => setField(k, e.target.value)}
          className="w-full border border-black/15 p-3 text-sm outline-none focus:border-black bg-[#FAFAFA]"
        />
      ) : (
        <input
          data-testid={`content-${k}`}
          type={type}
          value={form[k] ?? ""}
          onChange={(e) => setField(k, type === "number" ? Number(e.target.value) : e.target.value)}
          className="w-full border-b border-black/20 py-2 outline-none focus:border-black bg-transparent"
        />
      )}
      {helper && <p className="text-[10px] text-[#6B6B6B] mt-1">{helper}</p>}
    </div>
  );

  return (
    <div data-testid="admin-content">
      <div className="flex justify-end mb-6">
        <button data-testid="content-save" onClick={save} disabled={saving} className="btn-editorial">
          <Save size={14} className="mr-2" />{saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <Section title="Landing Hero">
        <Field label="Hero Headline (line 1)" k="hero_headline_1" helper={`e.g. "The Art of"`} />
        <Field label="Hero Headline (italic gold word)" k="hero_headline_2" helper={`e.g. "Indian"`} />
        <Field label="Hero Headline (line 3)" k="hero_headline_3" helper={`e.g. "Fashion Illustration."`} />
        <Field label="Hero Subtitle" k="hero_subtitle" multi />
        <Field label="Hero Module Quote (small card)" k="hero_module_quote" />
        <Field label="Editorial Quote (image strip)" k="editorial_quote" />
      </Section>

      <Section title="About Page">
        <Field label="About Headline (prefix)" k="about_headline_prefix" helper={`e.g. "An illustrator's"`} />
        <Field label="About Headline (italic gold word)" k="about_headline_italic" helper={`e.g. "quiet"`} />
        <Field label="About Headline (suffix)" k="about_headline_suffix" helper={`e.g. "studio."`} />
        <Field label="About Intro Paragraph" k="about_intro" multi />
        <Field label="Practice (short block)" k="about_practice" multi />
        <Field label="Teaching (short block)" k="about_teaching" multi />
        <Field label="Belief / Quote" k="about_belief" multi />
      </Section>

      <Section title="Course">
        <Field label="Course Title" k="course_title" helper={`Use "—" to split gold italic part, e.g. "Indian Fashion Illustration — The Complete Course"`} />
        <Field label="Course Description" k="course_description" multi />
        <div>
          <label className="overline block mb-2">What You'll Learn (one per line)</label>
          <textarea
            data-testid="content-learn-list"
            rows={7}
            value={learnStr}
            onChange={(e) => setField("course_what_you_learn", e.target.value)}
            className="w-full border border-black/15 p-3 text-sm outline-none focus:border-black bg-[#FAFAFA]"
          />
        </div>
      </Section>

      <Section title="Pricing">
        <div className="grid grid-cols-2 gap-6">
          <Field label="Price INR (₹)" k="price_inr" type="number" />
          <Field label="Price USD ($)" k="price_usd" type="number" />
        </div>
      </Section>

      <div className="flex justify-end mt-6">
        <button data-testid="content-save-bottom" onClick={save} disabled={saving} className="btn-editorial">
          <Save size={14} className="mr-2" />{saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

// ==================== IMAGES PANEL ====================
function ImagesPanel() {
  const { settings, reload } = useSettings();
  const [uploading, setUploading] = useState(null);

  const upload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("field", field);
      fd.append("file", file);
      await api.post("/admin/site-settings/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await reload();
      toast.success("Image updated");
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setUploading(null); }
  };

  const clear = async (field) => {
    try {
      await api.put("/admin/site-settings", { [field]: "" });
      await reload();
      toast.success("Reset to default image");
    } catch { toast.error("Failed"); }
  };

  const slots = [
    { field: "hero_image_path", label: "Hero Image (landing page, right side)", note: "Portrait 3:4 ratio recommended · appears next to hero headline" },
    { field: "about_portrait_path", label: "About Portrait (your photo)", note: "Portrait 3:4 · appears on About page" },
    { field: "gallery_1_path", label: "Gallery Image 1 (landing strip, left)", note: "Portrait 3:4 · appears in the editorial quote strip" },
    { field: "gallery_2_path", label: "Gallery Image 2 (landing strip, right)", note: "Portrait 3:4" },
  ];

  if (!settings) return <div>Loading…</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-images">
      {slots.map((s) => {
        const current = settings[s.field];
        const preview = resolveImage(current, null);
        return (
          <div key={s.field} className="border border-black/10 bg-white p-6">
            <p className="overline">{s.label}</p>
            <p className="text-[10px] text-[#6B6B6B] mt-1">{s.note}</p>
            <div className="mt-4 aspect-[3/4] bg-[#F2F0EB] border border-black/10 relative overflow-hidden">
              {preview ? (
                <img src={preview} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#6B6B6B] text-xs">
                  Default image in use
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <label className="btn-editorial-outline cursor-pointer" data-testid={`upload-${s.field}`}>
                <Upload size={14} className="mr-2" />{uploading === s.field ? "Uploading…" : "Upload"}
                <input hidden type="file" accept="image/*" onChange={(e) => upload(s.field, e.target.files[0])} />
              </label>
              {current && (
                <button data-testid={`reset-${s.field}`} onClick={() => clear(s.field)} className="overline link-underline">Reset to default</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ==================== FAQs PANEL ====================
function FAQsPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", order: 0, is_published: true });
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/faqs").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/faqs", { ...form, order: Number(form.order) });
      toast.success("FAQ added");
      setForm({ question: "", answer: "", order: rows.length, is_published: true });
      load();
    } catch { toast.error("Failed"); }
  };

  const update = async (id, patch) => {
    try {
      await api.put(`/admin/faqs/${id}`, patch);
      toast.success("Updated");
      setEditing(null);
      load();
    } catch { toast.error("Update failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await api.delete(`/admin/faqs/${id}`);
    load();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="admin-faqs">
      <form onSubmit={add} className="border border-black/10 bg-white p-6 space-y-4">
        <p className="overline"><span className="gold-line mr-3" />Add FAQ</p>
        <div>
          <label className="overline block mb-2">Question</label>
          <input required data-testid="faq-question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className="w-full border-b border-black/20 py-2 outline-none focus:border-black" />
        </div>
        <div>
          <label className="overline block mb-2">Answer</label>
          <textarea required rows={4} data-testid="faq-answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className="w-full border border-black/15 p-3 text-sm outline-none focus:border-black" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Order</label>
            <input type="number" data-testid="faq-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-full border-b border-black/20 py-2 outline-none focus:border-black" />
          </div>
          <label className="flex items-end gap-2 pb-2">
            <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
            <span className="text-xs">Published</span>
          </label>
        </div>
        <button data-testid="faq-add-submit" className="btn-editorial">
          <Plus size={14} className="mr-2" />Add FAQ
        </button>
      </form>

      <div className="space-y-4">
        {rows.length === 0 && <p className="text-sm text-[#6B6B6B]">No FAQs yet.</p>}
        {rows.map((f) => (
          <div key={f.id} className="border border-black/10 bg-white p-5" data-testid={`faq-row-${f.id}`}>
            {editing === f.id ? (
              <EditFaqForm faq={f} onCancel={() => setEditing(null)} onSave={(patch) => update(f.id, patch)} />
            ) : (
              <>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="text-sm font-medium">{f.question}</p>
                    <p className="overline text-[10px] mt-1">Order: {f.order} · {f.is_published ? "Published" : "Hidden"}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setEditing(f.id)} className="overline link-underline">Edit</button>
                    <button onClick={() => del(f.id)} className="text-[#6B6B6B] hover:text-red-600" data-testid={`faq-delete-${f.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-[#3a3a3a] mt-3 whitespace-pre-wrap">{f.answer}</p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function EditFaqForm({ faq, onCancel, onSave }) {
  const [form, setForm] = useState({ ...faq });
  return (
    <div className="space-y-3">
      <input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className="w-full border-b border-black/20 py-2 outline-none focus:border-black" />
      <textarea rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className="w-full border border-black/15 p-3 text-sm outline-none focus:border-black" />
      <div className="grid grid-cols-2 gap-3">
        <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="w-full border-b border-black/20 py-2 outline-none focus:border-black" />
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />Published</label>
      </div>
      <div className="flex gap-3">
        <button onClick={() => onSave({ question: form.question, answer: form.answer, order: form.order, is_published: form.is_published })} className="btn-editorial">Save</button>
        <button onClick={onCancel} className="overline link-underline">Cancel</button>
      </div>
    </div>
  );
}

